import React from "react";
import "./CPInput.css";

/**
 * Shared CrewPilot text input — canonical ordinary field.
 *
 * Public API:
 * - props: className, label, value, onChange(value), placeholder, type,
 *   required, disabled, readOnly, inputRef, id, name, autoComplete,
 *   inputMode, min, max, step, maxLength, pattern, voiceFieldId,
 *   voiceActive, onBlur, onFocus, onKeyDown, aria-label / aria-* via rest
 * - onChange receives the string value (not the event), matching CPDropdown
 *
 * Ordinary types: text, email, tel, url, number, password, date, time,
 * datetime-local, search (when a full labeled field is appropriate).
 * Do not use for checkbox/radio/file/hidden/range — those stay native.
 */
export function CPInput({
  className = "",
  label,
  value,
  onChange,
  placeholder = "",
  type = "text",
  required = false,
  disabled = false,
  readOnly = false,
  inputRef,
  id,
  name,
  autoComplete,
  inputMode,
  min,
  max,
  step,
  maxLength,
  pattern,
  voiceFieldId,
  voiceActive = false,
  onBlur,
  onFocus,
  onKeyDown,
  ...rest
}) {
  const groupClass = [
    "cp-input-group",
    voiceActive ? "cp-input-group--voice-active" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const inputClass = [
    "cp-input",
    voiceActive ? "cp-input--voice-active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={groupClass} data-voice-field={voiceFieldId}>
      {label && (
        <label className="cp-input-label" htmlFor={id}>
          {label}
          {required && <span className="cp-input-required">*</span>}
        </label>
      )}

      <input
        ref={inputRef}
        id={id}
        name={name}
        className={inputClass}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        autoComplete={autoComplete}
        inputMode={inputMode}
        min={min}
        max={max}
        step={step}
        maxLength={maxLength}
        pattern={pattern}
        onChange={
          disabled || readOnly ? undefined : (e) => onChange?.(e.target.value)
        }
        onBlur={disabled || readOnly ? undefined : onBlur}
        onFocus={disabled ? undefined : onFocus}
        onKeyDown={disabled ? undefined : onKeyDown}
        {...rest}
      />
    </div>
  );
}
